// ─── TeacherClassMedia.jsx ──────────────────────────────────────────────────
// Lets a class teacher send photos/videos to the parents of their class —
// broadcast to everyone in the class+section, or to one individual parent.
import { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import {
  PhotoIcon, FilmIcon, TrashIcon, UsersIcon, UserGroupIcon,
  MagnifyingGlassIcon, CloudArrowUpIcon, PaperAirplaneIcon,
} from '@heroicons/react/24/outline';

const style = `
  @import url('https://fonts.googleapis.com/css2?family=Sora:wght@300;400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');
  .tcm * { box-sizing:border-box; }
  .tcm { font-family:'Sora',sans-serif; color:#1a1d2e; animation:tcmFade .4s ease both; }
  @keyframes tcmFade { from{opacity:0;transform:translateY(10px)} to{opacity:1;transform:translateY(0)} }
  .tcm-header { display:flex; align-items:flex-start; justify-content:space-between; margin-bottom:1.5rem; flex-wrap:wrap; gap:1rem; }
  .tcm-title { font-size:1.35rem; font-weight:700; letter-spacing:-.02em; color:#0f1123; }
  .tcm-sub   { font-size:.76rem; color:#9094a8; margin-top:3px; }

  .btn { display:inline-flex; align-items:center; gap:7px; padding:9px 16px; border:none; border-radius:10px; font-family:'Sora',sans-serif; font-size:.8rem; font-weight:600; cursor:pointer; transition:all .18s; }
  .btn svg { width:15px; height:15px; stroke-width:2; }
  .btn-blue  { background:#3b6ef8; color:white; }
  .btn-blue:hover  { background:#2d5ef0; box-shadow:0 4px 14px rgba(59,110,248,.35); transform:translateY(-1px); }
  .btn-green { background:#10c98e; color:white; }
  .btn-green:hover { background:#0db87e; }
  .btn-ghost { background:#f0f2f7; color:#6b7194; }
  .btn-ghost:hover { background:#e4e7f0; }
  .btn-red   { background:#fef2f2; color:#ef4444; border:1px solid #fecaca; }
  .btn-red:hover { background:#ef4444; color:white; }
  .btn-sm { padding:6px 12px; font-size:.74rem; }
  .btn:disabled { opacity:.5; cursor:not-allowed; transform:none !important; }

  .panel { background:white; border-radius:16px; border:1px solid #e8ebf4; overflow:hidden; margin-bottom:1.2rem; }
  .panel-head { display:flex; align-items:center; justify-content:space-between; padding:14px 18px; border-bottom:1px solid #f0f2f8; flex-wrap:wrap; gap:8px; }
  .panel-title { font-size:.86rem; font-weight:600; color:#1a1d2e; }
  .panel-body { padding:18px; }

  .form-field { display:flex; flex-direction:column; gap:5px; margin-bottom:14px; }
  .form-label { font-size:.68rem; font-weight:700; color:#9094a8; text-transform:uppercase; letter-spacing:.06em; }
  .form-input,.form-select,.form-textarea { font-family:'Sora',sans-serif; font-size:.82rem; padding:9px 11px; border:1.5px solid #e0e4f0; border-radius:9px; background:#f8f9fc; color:#1a1d2e; outline:none; width:100%; }
  .form-input:focus,.form-select:focus,.form-textarea:focus { border-color:#3b6ef8; background:white; }
  .form-textarea { resize:vertical; min-height:64px; }

  .combo-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(170px,1fr)); gap:.7rem; margin-bottom:14px; }
  .combo-card { background:#fafbff; border-radius:12px; border:1.5px solid #e8ebf4; padding:.8rem .9rem; cursor:pointer; transition:all .15s; }
  .combo-card:hover { border-color:#3b6ef8; }
  .combo-card.active { border-color:#3b6ef8; background:#f0f4ff; }
  .combo-name { font-size:.84rem; font-weight:700; color:#0f1123; }
  .combo-sec  { font-size:.72rem; color:#9094a8; margin-top:2px; }

  .target-toggle { display:flex; gap:6px; background:#f0f2f7; border-radius:10px; padding:4px; margin-bottom:14px; }
  .target-toggle button { flex:1; padding:8px 0; border-radius:7px; border:none; font-family:'Sora',sans-serif; font-size:.78rem; font-weight:600; cursor:pointer; display:flex; align-items:center; justify-content:center; gap:6px; background:transparent; color:#6b7194; transition:all .15s; }
  .target-toggle button svg { width:14px; height:14px; stroke-width:2; }
  .target-toggle button.active { background:white; color:#3b6ef8; box-shadow:0 1px 4px rgba(0,0,0,.1); }

  .parent-list { max-height:220px; overflow-y:auto; border:1.5px solid #e0e4f0; border-radius:10px; padding:6px; }
  .parent-row { display:flex; align-items:center; gap:9px; padding:7px 8px; border-radius:8px; cursor:pointer; font-size:.8rem; }
  .parent-row:hover { background:#f5f7fc; }
  .parent-row.sel { background:#eef2ff; }
  .parent-row input { accent-color:#3b6ef8; }
  .parent-name { font-weight:600; color:#0f1123; }
  .parent-child { font-size:.7rem; color:#9094a8; }

  .dropzone { border:2px dashed #d0d5e8; border-radius:12px; padding:22px; text-align:center; cursor:pointer; background:#fafbfc; transition:all .15s; }
  .dropzone.over { border-color:#3b6ef8; background:#f0f4ff; }
  .dropzone-icon { font-size:1.8rem; margin-bottom:6px; }

  .hist-grid { display:grid; grid-template-columns:repeat(auto-fill,minmax(220px,1fr)); gap:12px; padding:14px; }
  .hist-card { border:1px solid #edf0f9; border-radius:12px; overflow:hidden; background:#fafbff; display:flex; flex-direction:column; }
  .hist-thumb { width:100%; height:140px; object-fit:cover; background:#e8ebf4; display:block; }
  .hist-video-thumb { width:100%; height:140px; display:flex; flex-direction:column; align-items:center; justify-content:center; gap:6px; background:#eef0fb; color:#8b5cf6; }
  .hist-video-thumb svg { width:30px; height:30px; stroke-width:1.6; }
  .hist-body { padding:10px 12px; display:flex; flex-direction:column; gap:6px; flex:1; }
  .hist-caption { font-size:.78rem; color:#1a1d2e; line-height:1.4; }
  .hist-meta { font-size:.68rem; color:#9094a8; display:flex; flex-direction:column; gap:2px; }
  .hist-target { display:inline-flex; align-self:flex-start; align-items:center; gap:4px; font-size:.64rem; font-weight:700; padding:2px 8px; border-radius:99px; background:#ecfdf5; color:#10c98e; }
  .hist-target.individual { background:#eef2ff; color:#3b6ef8; }
  .hist-foot { display:flex; justify-content:flex-end; margin-top:auto; }

  .tcm-empty { text-align:center; padding:3rem 2rem; }
  .tcm-empty-icon { width:48px; height:48px; border-radius:13px; background:#eef2ff; color:#3b6ef8; display:flex; align-items:center; justify-content:center; margin:0 auto 12px; }
  .tcm-empty-icon svg { width:22px; height:22px; stroke-width:1.6; }
  .tcm-empty-title { font-size:.9rem; font-weight:600; color:#1a1d2e; margin-bottom:4px; }
  .tcm-empty-sub { font-size:.76rem; color:#9094a8; }

  .tcm-loading { display:flex; align-items:center; justify-content:center; height:200px; gap:10px; color:#9094a8; }
  .spinner { width:18px; height:18px; border:2px solid #e4e7f0; border-top-color:#3b6ef8; border-radius:50%; animation:spin .7s linear infinite; }
  @keyframes spin { to{transform:rotate(360deg);} }

  @media (max-width:768px) {
    .tcm-title { font-size:1.1rem; }
  }
`;

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric',hour:'2-digit',minute:'2-digit'}) : '—';
const fmtSize = (b) => b ? (b > 1024*1024 ? `${(b/1024/1024).toFixed(1)} MB` : `${(b/1024).toFixed(0)} KB`) : null;
const fileIcon = (f) => f?.type?.startsWith('image/') ? '🖼️' : f?.type?.startsWith('video/') ? '🎬' : '📁';

const TeacherClassMedia = () => {
  const [loadingClasses, setLoadingClasses] = useState(true);
  const [combos,         setCombos]         = useState([]); // [{classId,className,section}]
  const [selectedCombo,  setSelectedCombo]  = useState(null);

  const [targetType,       setTargetType]       = useState('all'); // 'all' | 'individual'
  const [parents,          setParents]          = useState([]);
  const [loadingParents,   setLoadingParents]   = useState(false);
  const [parentSearch,     setParentSearch]     = useState('');
  const [targetParentId,   setTargetParentId]   = useState('');

  const [caption,      setCaption]      = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [dragOver,     setDragOver]     = useState(false);
  const [sending,      setSending]      = useState(false);
  const [uploadPct,    setUploadPct]    = useState(0);

  const [history,        setHistory]        = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);

  useEffect(() => {
    api.get('/teacher/class-media/my-classes')
      .then(({ data }) => {
        const list = (data || []).flatMap(c => (c.sections || []).map(s => ({
          classId: c.classId, className: c.className, section: s,
        })));
        setCombos(list);
        if (list.length === 1) setSelectedCombo(list[0]);
      })
      .catch(() => toast.error('Failed to load your classes'))
      .finally(() => setLoadingClasses(false));

    fetchHistory();
  }, []);

  const fetchHistory = () => {
    setLoadingHistory(true);
    api.get('/teacher/class-media')
      .then(({ data }) => setHistory(data || []))
      .catch(() => toast.error('Failed to load sent history'))
      .finally(() => setLoadingHistory(false));
  };

  const fetchParents = useCallback(() => {
    if (!selectedCombo) return;
    setLoadingParents(true);
    api.get('/teacher/class-media/parents', { params: { classId: selectedCombo.classId, section: selectedCombo.section } })
      .then(({ data }) => setParents(data || []))
      .catch(() => toast.error('Failed to load parents'))
      .finally(() => setLoadingParents(false));
  }, [selectedCombo]);

  useEffect(() => {
    if (targetType === 'individual' && selectedCombo) fetchParents();
  }, [targetType, selectedCombo, fetchParents]);

  const pickCombo = (combo) => {
    setSelectedCombo(combo);
    setTargetParentId('');
    setParents([]);
  };

  const handleFileSelect = (file) => {
    if (!file) return;
    if (!file.type.startsWith('image/') && !file.type.startsWith('video/')) {
      toast.error('Please select an image or video file');
      return;
    }
    setSelectedFile(file);
  };

  const resetForm = () => {
    setCaption('');
    setSelectedFile(null);
    setUploadPct(0);
    setTargetType('all');
    setTargetParentId('');
  };

  const handleSend = async () => {
    if (!selectedCombo) { toast.error('Select a class & section first'); return; }
    if (!selectedFile) { toast.error('Please select a photo or video'); return; }
    if (targetType === 'individual' && !targetParentId) { toast.error('Select a parent to send to'); return; }

    try {
      setSending(true);
      const fd = new FormData();
      fd.append('classId', selectedCombo.classId);
      fd.append('section', selectedCombo.section);
      fd.append('caption', caption);
      fd.append('targetType', targetType);
      if (targetType === 'individual') fd.append('targetParentId', targetParentId);
      fd.append('file', selectedFile);

      const { data } = await api.post('/teacher/class-media', fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (e) => setUploadPct(Math.round((e.loaded * 100) / e.total)),
      });

      setHistory(h => [data, ...h]);
      toast.success(targetType === 'all' ? 'Sent to all parents in the class' : 'Sent to parent');
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send');
    } finally {
      setSending(false);
      setUploadPct(0);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this photo/video? Parents will no longer be able to see it.')) return;
    try {
      await api.delete(`/teacher/class-media/${id}`);
      setHistory(h => h.filter(m => m._id !== id));
      toast.success('Deleted');
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to delete');
    }
  };

  const filteredParents = parents.filter(p =>
    !parentSearch.trim() ||
    p.name?.toLowerCase().includes(parentSearch.toLowerCase()) ||
    p.childName?.toLowerCase().includes(parentSearch.toLowerCase())
  );

  if (loadingClasses) return (
    <div className="tcm"><style>{style}</style>
      <div className="tcm-loading"><div className="spinner" />Loading…</div>
    </div>
  );

  if (combos.length === 0) return (
    <div className="tcm"><style>{style}</style>
      <div className="tcm-header">
        <div><div className="tcm-title">Class Gallery</div><div className="tcm-sub">Share photos and videos with your class parents</div></div>
      </div>
      <div className="panel">
        <div className="tcm-empty">
          <div className="tcm-empty-icon"><PhotoIcon /></div>
          <div className="tcm-empty-title">You are not a class teacher</div>
          <div className="tcm-empty-sub">This feature is only available to teachers assigned as a class teacher for a class &amp; section.</div>
        </div>
      </div>
    </div>
  );

  return (
    <div className="tcm"><style>{style}</style>
      <div className="tcm-header">
        <div><div className="tcm-title">Class Gallery</div><div className="tcm-sub">Share photos and videos with your class parents</div></div>
      </div>

      <div className="panel">
        <div className="panel-head"><div className="panel-title">Send New Photo / Video</div></div>
        <div className="panel-body">
          {combos.length > 1 && (
            <div className="form-field">
              <label className="form-label">Class &amp; Section</label>
              <div className="combo-grid">
                {combos.map(c => (
                  <div key={`${c.classId}-${c.section}`} className={`combo-card${selectedCombo?.classId===c.classId && selectedCombo?.section===c.section ? ' active' : ''}`} onClick={() => pickCombo(c)}>
                    <div className="combo-name">{c.className}</div>
                    <div className="combo-sec">Section {c.section}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {combos.length === 1 && (
            <div className="form-field">
              <label className="form-label">Sending as class teacher of</label>
              <div className="combo-card active" style={{cursor:'default',display:'inline-block'}}>
                <div className="combo-name">{combos[0].className}</div>
                <div className="combo-sec">Section {combos[0].section}</div>
              </div>
            </div>
          )}

          <div className="form-field">
            <label className="form-label">Photo or Video *</label>
            <div className={`dropzone${dragOver ? ' over' : ''}`}
              onDragOver={e => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={e => { e.preventDefault(); setDragOver(false); handleFileSelect(e.dataTransfer.files[0]); }}
              onClick={() => document.getElementById('tcm-file-input').click()}>
              <input id="tcm-file-input" type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={e => handleFileSelect(e.target.files[0])} />
              {selectedFile ? (
                <div>
                  <div className="dropzone-icon">{fileIcon(selectedFile)}</div>
                  <div style={{ fontWeight: 600, fontSize: '.82rem' }}>{selectedFile.name}</div>
                  <div style={{ fontSize: '.72rem', color: '#9094a8', marginTop: 3 }}>{fmtSize(selectedFile.size)}</div>
                  {selectedFile.type.startsWith('image/') && (
                    <img src={URL.createObjectURL(selectedFile)} alt="preview" style={{ maxHeight: 140, maxWidth: '100%', marginTop: 10, borderRadius: 8, border: '1px solid #e8ebf4' }} />
                  )}
                </div>
              ) : (
                <div>
                  <div className="dropzone-icon">☁️</div>
                  <div style={{ fontWeight: 600, fontSize: '.82rem', color: '#1a1d2e' }}>Tap to select a photo or video</div>
                  <div style={{ fontSize: '.72rem', color: '#9094a8', marginTop: 4 }}>JPG, PNG, MP4, MOV…</div>
                </div>
              )}
            </div>
            {sending && uploadPct > 0 && (
              <div style={{ marginTop: 8 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '.72rem', color: '#6b7194', marginBottom: 4 }}><span>Uploading…</span><span>{uploadPct}%</span></div>
                <div style={{ height: 6, background: '#f0f2f7', borderRadius: 99, overflow: 'hidden' }}><div style={{ height: '100%', background: '#3b6ef8', borderRadius: 99, width: `${uploadPct}%`, transition: 'width .3s' }} /></div>
              </div>
            )}
          </div>

          <div className="form-field">
            <label className="form-label">Caption (optional)</label>
            <textarea className="form-textarea" value={caption} onChange={e => setCaption(e.target.value)} placeholder="Say something about this photo/video…" />
          </div>

          <div className="form-field">
            <label className="form-label">Send To</label>
            <div className="target-toggle">
              <button type="button" className={targetType === 'all' ? 'active' : ''} onClick={() => setTargetType('all')}><UserGroupIcon />Send to All</button>
              <button type="button" className={targetType === 'individual' ? 'active' : ''} onClick={() => setTargetType('individual')}><UsersIcon />Send to Individual</button>
            </div>
          </div>

          {targetType === 'individual' && (
            <div className="form-field">
              <label className="form-label">Parent {targetParentId && '(1 selected)'}</label>
              <div style={{ position: 'relative', marginBottom: 8 }}>
                <MagnifyingGlassIcon style={{ width: 15, height: 15, position: 'absolute', left: 10, top: 10, color: '#9094a8' }} />
                <input type="text" className="form-input" style={{ paddingLeft: 32 }} placeholder="Search parent or child name…" value={parentSearch} onChange={e => setParentSearch(e.target.value)} />
              </div>
              {loadingParents ? (
                <div style={{ fontSize: '.78rem', color: '#9094a8', padding: '8px 0' }}>Loading parents…</div>
              ) : filteredParents.length === 0 ? (
                <div style={{ fontSize: '.78rem', color: '#9094a8', padding: '8px 0' }}>No parents found.</div>
              ) : (
                <div className="parent-list">
                  {filteredParents.map(p => (
                    <label key={p.parentId} className={`parent-row${targetParentId === String(p.parentId) ? ' sel' : ''}`}>
                      <input type="radio" name="tcm-target-parent" checked={targetParentId === String(p.parentId)} onChange={() => setTargetParentId(String(p.parentId))} />
                      <div>
                        <div className="parent-name">{p.name}</div>
                        <div className="parent-child">parent of {p.childName} · {p.email}</div>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <button className="btn btn-blue" onClick={handleSend} disabled={sending} style={{ marginTop: 4 }}>
            <PaperAirplaneIcon />{sending ? (uploadPct > 0 ? `Sending ${uploadPct}%` : 'Sending…') : 'Send'}
          </button>
        </div>
      </div>

      <div className="panel">
        <div className="panel-head"><div className="panel-title">Sent History ({history.length})</div></div>
        {loadingHistory ? (
          <div className="tcm-loading"><div className="spinner" />Loading…</div>
        ) : history.length === 0 ? (
          <div className="tcm-empty">
            <div className="tcm-empty-icon"><CloudArrowUpIcon /></div>
            <div className="tcm-empty-title">Nothing sent yet</div>
            <div className="tcm-empty-sub">Photos and videos you send will show up here.</div>
          </div>
        ) : (
          <div className="hist-grid">
            {history.map(m => (
              <div key={m._id} className="hist-card">
                {m.mediaType === 'image' ? (
                  <img className="hist-thumb" src={m.fileUrl} alt={m.caption || 'shared media'} />
                ) : (
                  <div className="hist-video-thumb"><FilmIcon /><span style={{ fontSize: '.68rem', fontWeight: 600 }}>Video</span></div>
                )}
                <div className="hist-body">
                  <span className={`hist-target${m.targetType === 'individual' ? ' individual' : ''}`}>
                    {m.targetType === 'all' ? 'All Parents' : (m.targetParentName || 'Individual')}
                  </span>
                  {m.caption && <div className="hist-caption">{m.caption}</div>}
                  <div className="hist-meta">
                    <span>{m.className}{m.section ? ` · Section ${m.section}` : ''}</span>
                    <span>{fmtDate(m.createdAt)}{m.fileSize ? ` · ${fmtSize(m.fileSize)}` : ''}</span>
                  </div>
                  <div className="hist-foot">
                    <button className="btn btn-red btn-sm" onClick={() => handleDelete(m._id)}><TrashIcon />Delete</button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherClassMedia;
